import z from 'zod';
import { API } from 'aws-amplify';
import { IAddress, getAddressSchema } from '../address';
import { IPhone } from '../phone';
import { isAIActive } from './constants';
import { processResponse } from '../../utils/api';
import { cleanObject } from 'src/utils';
import { ISummary } from 'src/types';
import { IFieldErrors, ValidationError } from '../validation';
import { diff } from 'deep-object-diff';
import { disclaimerErrorMap, EmailSchema, UrlSchema } from '../validation';
import { TYPE_CODE_K12, TYPE_CODE_OTHER_ED } from './constants';
import { IRequestMeta } from './request';

interface IOrg {
  orgId?: string;
  name: string;
  email: string;
  phone: Partial<IPhone>;
  fax?: Partial<IPhone>;
  website?: string;
  visitingAddress: IAddress;
  shippingAddress?: IAddress;
  orgTypeCd: number;
  orgSubTypeCd: string;
}

export interface IOrgAI extends IOrg {
  aiCode: string;
  aiStatusCd: '1' | '2';
  aiLevelCd: '1' | '2';
  lowestGradeCd?: string;
  highestGradeCd?: string;
  aiCategoryCd?: string;
}

export interface IAIUpdate extends IOrgAI {
  isMerge?: boolean;
  merge?: {
    aiCode?: string;
    name?: string;
    address?: IAddress;
  };
  closureDate?: string;
  submitterComment?: string;
  deactivateDisclaimer?: boolean;
}

export interface IAIUpdateRequest {
  data: IAIUpdate;
  meta: IRequestMeta;
  original: Partial<IAIUpdate>;
  changes: Partial<IAIUpdate>;
}

export interface IOrgUpdate {
  creator: string;
  creatorEmail: string;
  orgId: string;
  name: string;
  email: string;
  phone: Partial<IPhone>;
  fax: Partial<IPhone>;
  website: string;
  isMerge?: boolean;
  merge?: {
    aiCode?: string;
    name?: string;
    address?: IAddress;
  };
  visitingAddress: IAddress;
  shippingAddress?: IAddress;
  closureDate?: string;
  lowestGradeCd?: string;
  highestGradeCd?: string;
  orgSubTypeCd: string;
  aiCode: string;
  aiStatusCd: '1' | '2';
  aiLevelCd: '1' | '2';
  disclaimer: boolean;
  submitter: string;
  aiCategoryCd?: string;
}

const MetaSchema = z.object({
  creator: z.string().min(1),
  creatorEmail: EmailSchema,
  disclaimer: z.literal(true, {
    errorMap: disclaimerErrorMap,
  }),
  submitter: z.string().min(1),
});

const baseSchema = z.object({
  orgId: z.string().min(1),
  name: z.string().min(1),
  email: EmailSchema,
  phone: z.object({
    formatted: z.string().min(7).max(22),
  }),
  website: UrlSchema,
  aiCode: z.string().length(6, 'Field must be a code 6 characters long'),
  aiStatusCd: z.enum(['1', '2']),
  aiLevelCd: z.enum(['1', '2']),
  orgTypeCd: z.number(),
  orgSubTypeCd: z.string(),
  isMerge: z.boolean().optional(),
  aiCategoryCd: z.string().optional(),
});

const ClosureDateSchema = z.string().regex(/\d{1,2}\/\d{1,2}\/\d{4}/, 'Closure date must be in a format of DD/MM/YYYY');
const getGradeSchema = (orgTypeCd: number | undefined) => {
  const string = z.string();
  if (orgTypeCd === TYPE_CODE_K12) {
    return string;
  } else if (orgTypeCd === TYPE_CODE_OTHER_ED) {
    return string.optional();
  } else {
    return z.undefined();
  }
};

/**
 * Adding new validation check to comply with this database trigger: K12.HIGHEST_GRADE_LVL_CD/LOWEST_GRADE_LVL_CD
 * If lowest grade is selected from dropdown,the highest grade also need to be selected from its dropdown and vice versa
 * If lowest grade is not selected, then highest need not be selected and vice versa
 *
 * @param highestGradeCd
 * @param lowestGradeCd
 */
const getHighestLowestGradesSchema = (highestGradeCd: string | undefined, lowestGradeCd: string | undefined) => {
  if ((highestGradeCd && !lowestGradeCd) || (!highestGradeCd && lowestGradeCd)) {
    return z.string();
  } else if ((!highestGradeCd && !lowestGradeCd) || (highestGradeCd && lowestGradeCd)) {
    return z.string().optional();
  } else {
    return z.undefined();
  }
};

const getDataSchema = (update: Partial<IAIUpdate>) => {
  const { visitingAddress, shippingAddress, merge, aiStatusCd } = update;
  const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);
  const ShippingAddressSchema = getAddressSchema(shippingAddress?.countryIsoCd);
  const MergeAddressSchema = getAddressSchema(merge?.address?.countryIsoCd);

  const MergeSchema = z
    .object({
      aiCode: z.string().min(6),
      name: z.string().min(1),
      address: MergeAddressSchema,
    })
    .partial()
    .refine(data => {
      return !!data.aiCode || (!!data.name && !!data.address);
    }, 'Requires either an AI Code or School Name & Address');

  const isClosureOptional = !aiStatusCd || isAIActive(aiStatusCd);

  return baseSchema.extend({
    visitingAddress: VisitingAddressSchema,
    shippingAddress: ShippingAddressSchema.optional(),
    merge: MergeSchema.optional(),
    closureDate: isClosureOptional ? ClosureDateSchema.optional() : ClosureDateSchema,
    submitterComment: shippingAddress ? z.string().min(1) : z.string().optional(),
    deactivateDisclaimer: isClosureOptional
      ? z.undefined()
      : z.literal(true, {
          errorMap: disclaimerErrorMap,
        }),
    lowestGradeCd: getHighestLowestGradesSchema(update.highestGradeCd, update.lowestGradeCd),
    highestGradeCd: getHighestLowestGradesSchema(update.highestGradeCd, update.lowestGradeCd),
    fax: z
      .object({
        formatted: z.string().min(7).max(22),
      })
      .optional(),
  });
};

/**
 * Returns the Zod Validation Schema for the update request.  This is a factory
 * method due to the fact that it contains conditional validations and the schema
 * needs to be built based off certain selections
 */
const getSchema = (update: Partial<IAIUpdate>) => {
  const dataSchema = getDataSchema(update || {});
  return z.object({
    data: dataSchema,
    meta: MetaSchema,
  });
};

export const validate = (
  update: Partial<IAIUpdate>,
  meta: Partial<IRequestMeta>,
  original: Partial<IAIUpdate>,
): IAIUpdateRequest => {
  const schema = getSchema(update);
  try {
    // Ensure there are changes from the original record
    const validated = schema.parse({ data: update, meta: meta });
    const changes = diff(cleanObject(original), cleanObject(validated.data));
    const hasChanges = Object.keys(changes).length > 0;
    if (!hasChanges) {
      throw new z.ZodError([
        {
          code: 'invalid_type',
          expected: 'object',
          received: 'undefined',
          path: ['diff'],
          message: 'No changes were detected to this organization',
        },
      ]);
    }

    return {
      ...validated,
      original,
      changes,
    };
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};

export const create = async (
  update: Partial<IAIUpdate>,
  meta: Partial<IRequestMeta>,
  original: Partial<IAIUpdate>,
): Promise<IAIUpdateRequest> => {
  const request = validate(update, meta, original);
  const response: IAIUpdateRequest = await API.post('AiFormRequest', '/save?key=submitUpdateRequest', {
    body: { ...request, recordCategoryType: 'ai' },
  }).then(response => processResponse(response));
  return response;
};

export interface IActiveRequest {
  ids: string[];
  recordCategoryType?: string;
}

export const active = async (params: IActiveRequest) => {
  const response: ISummary[] = await API.post('AiFormRequest', '/save?key=activeUpdateRequests', {
    body: { ...params, recordCategoryType: 'ai' },
  }).then(response => processResponse(response));
  return response;
};

/**
 *
 * @param params
 * @returns
 */
interface ISearchActive {
  active: ISummary[];
  owned: ISummary[];
}

export const searchActive = async (params: IActiveRequest) => {
  const response: ISearchActive = await API.post('AiFormRequest', '/save?key=getUpdateRequests', {
    body: { ...params, recordCategoryType: 'ai' },
  }).then(response => processResponse(response));
  return response;
};
